// @flow
/* eslint-disable react/jsx-no-bind */
import * as React from 'react';
import { Loader } from '@locus-taxy/neo-ui';

import useClientId from 'common/hooks/useClientId';
import { useToast, ToastTypes } from 'common/components/core/toast';
import usePersonnel from 'common/hooks/usePersonnel';

import useProductSettingsFormQuery from './useProductSettingsFormQuery';
import useUpdateProductSettings from './useUpdateProductSettings';

import { ApiStatusEnum } from 'common/enums/API';
import ProductSettingsFormBase from './ProductSettingsFormBase';
import { toTranslationKey } from './utils';

type Props = {
    product: string
};

const ProductSettingsForm = ({ product }: Props): React.Node => {
    const clientId = useClientId();
    const formQuery = useProductSettingsFormQuery({ clientId, product });
    const updateSettings = useUpdateProductSettings();
    const { showToast } = useToast();
    const personnel = usePersonnel();

    const isGlobalPersonnel = personnel.isGlobalPersonnel();

    if (formQuery.status === ApiStatusEnum.Loading) {
        return (
            <Loader size="large" width="100%" height="100%" color="blue.500" />
        );
    }

    if (!formQuery.data) {
        // TODO: Handle error
        return null;
    }

    const { data } = formQuery;

    // Hack: Adding the personal to settings to support RBAC for form fields.
    // This will be deleted when we save the settings data.
    const { settings } = data;
    settings.context = {
        isGlobalPersonnel,
        product,
        toTranslationKey
    };

    const onSave = value => {
        const settings = { ...value };
        delete settings.context;

        updateSettings.mutate(
            {
                clientId,
                settings,
                version: data.version,
                product
            },
            {
                onSuccess: () => {
                    showToast({
                        type: ToastTypes.Success,
                        message: 'Update settings done'
                    });
                },
                onError: err => {
                    showToast({
                        type: ToastTypes.Error,
                        message: err.message
                    });
                }
            }
        );
    };

    return (
        <ProductSettingsFormBase
            initialValue={settings}
            metadata={data.metadata}
            product={product}
            onSave={onSave}
            isSaving={updateSettings.status === ApiStatusEnum.Loading}
        />
    );
};

export default ProductSettingsForm;
